import AccountCircle from "@mui/icons-material/AccountCircle";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Menu,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import {
  Logout,
  useAuthProvider,
  useGetIdentity,
} from "react-admin";
import { UserMenuContext } from "ra-ui-materialui";
import { ReactNode, useCallback, useMemo, useState } from "react";

type ScmIdentity = {
  fullName?: string;
  nome_reduzido?: string;
  avatar?: string;
};

interface ScmUserMenuProps {
  children?: ReactNode;
}

const ScmUserMenu = ({ children }: ScmUserMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { isPending, identity: rawIdentity } = useGetIdentity();
  const identity = rawIdentity as ScmIdentity | undefined;
  const authProvider = useAuthProvider();
  const theme = useTheme();
  const isLargeEnough = useMediaQuery(theme.breakpoints.up("sm"));

  const menuChildren = children ?? (authProvider ? <Logout /> : null);
  const handleMenu = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = useCallback(() => setAnchorEl(null), []);
  const context = useMemo(() => ({ onClose: handleClose }), [handleClose]);

  if (!menuChildren) return null;

  const open = Boolean(anchorEl);
  const userLabel = identity?.fullName ?? "Perfil";

  const userIcon =
    !isPending && identity?.avatar ? (
      <Avatar
        className={classes.avatar}
        src={identity.avatar}
        alt={userLabel}
      />
    ) : (
      <AccountCircle />
    );

  return (
    <Root>
      {isLargeEnough && !isPending && identity?.fullName ? (
        <Button
          aria-label={userLabel}
          className={classes.userButton}
          color="inherit"
          startIcon={userIcon}
          onClick={handleMenu}
          variant="text"
        >
          {identity.nome_reduzido ? (
            <Box
              component="span"
              sx={{ fontSize: "0.75rem", lineHeight: 1.2, textAlign: "left" }}
            >
              <Box component="span" sx={{ display: "block" }}>
                {identity.nome_reduzido}
              </Box>
              {identity.fullName}
            </Box>
          ) : (
            identity.fullName
          )}
        </Button>
      ) : (
        <Tooltip title={userLabel}>
          <IconButton
            aria-label={userLabel}
            aria-owns={open ? "menu-appbar" : undefined}
            aria-haspopup
            color="inherit"
            onClick={handleMenu}
          >
            {userIcon}
          </IconButton>
        </Tooltip>
      )}
      <UserMenuContext.Provider value={context}>
        <Menu
          id="menu-appbar"
          disableScrollLock
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={open}
          onClose={handleClose}
        >
          {menuChildren}
        </Menu>
      </UserMenuContext.Provider>
    </Root>
  );
};

const classes = {
  avatar: "ScmUserMenu-avatar",
  userButton: "ScmUserMenu-userButton",
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.userButton}`]: {
    textTransform: "none",
    marginInlineStart: theme.spacing(0.5),
  },
  [`& .${classes.avatar}`]: {
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
}));

export default ScmUserMenu;
